import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Badge, Spinner, Alert,
  ProgressBar
} from 'react-bootstrap';
import {
  Shirt, Scissors, Ruler, AlertTriangle, Clock, CheckCircle,
  Users, DollarSign, TrendingUp, Calendar, ChevronRight,
  RefreshCw, Package, Target
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import customOrderService from '../../services/customOrderService';
import OrderStatusBadge from './commande/OrderStatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';

/**
 * ✅ ATELIER: Page d'accueil du module avec stats + raccourcis + aperçus.
 */
const AtelierDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAtelierEnabled, currentBoutique } = useBoutique();

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAtelierEnabled) {
      loadDashboard();
    }
  }, [isAtelierEnabled]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await customOrderService.getDashboard();

      if (response.success) {
        setDashboard(response.data);
      } else {
        setError(response.message || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Formatage monétaire
  const formatPrice = (value) => customOrderService.formatPrice(value);

  // Si atelier non activé
  if (!isAtelierEnabled) {
    return (
      <Container fluid className="py-4">
        <Card className="text-center py-5">
          <Card.Body>
            <Scissors size={64} className="text-muted mb-3" />
            <h4>Module Atelier non activé</h4>
            <p className="text-muted">
              Le module Atelier n'est pas activé pour cette boutique.
            </p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Chargement initial
  if (loading && !dashboard) {
    return (
      <Container fluid className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <div className="mt-3 text-muted">Chargement du dashboard...</div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* ============ EN-TÊTE ============ */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h1 className="h3 mb-1 d-flex align-items-center gap-2">
                <Scissors size={28} className="text-primary" />
                Atelier de Couture
              </h1>
              <p className="text-muted mb-0">
                Bienvenue {user?.firstName}, voici un aperçu de votre atelier
                {currentBoutique && ` — ${currentBoutique.name}`}
              </p>
            </div>
            <Button
              variant="outline-primary"
              onClick={loadDashboard}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spin me-1' : 'me-1'} />
              Actualiser
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ============ RACCOURCIS PRINCIPAUX ============ */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card 
            className="shortcut-card shadow-sm h-100"
            onClick={() => navigate('/atelier/orders')}
          >
            <Card.Body>
              <div className="d-flex align-items-center gap-3">
                <div className="shortcut-icon" style={{ 
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' 
                }}>
                  <Shirt size={28} color="white" />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">Commandes</h6>
                  <div className="small text-muted">
                    {dashboard?.activeOrders || 0} en cours
                  </div>
                </div>
                <ChevronRight size={20} className="text-muted" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card
            className="shortcut-card shadow-sm h-100"
            onClick={() => navigate('/atelier/models')}
          >
            <Card.Body>
              <div className="d-flex align-items-center gap-3">
                <div className="shortcut-icon" style={{ 
                  background: 'linear-gradient(135deg, #ec4899, #f43f5e)' 
                }}>
                  <Package size={28} color="white" />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">Modèles</h6>
                  <div className="small text-muted">
                    {dashboard?.totalModels || 0} au catalogue
                  </div>
                </div>
                <ChevronRight size={20} className="text-muted" />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card
            className="shortcut-card shadow-sm h-100"
            onClick={() => navigate('/atelier/measurements')}
          >
            <Card.Body>
              <div className="d-flex align-items-center gap-3">
                <div className="shortcut-icon" style={{ 
                  background: 'linear-gradient(135deg, #a855f7, #c026d3)' 
                }}>
                  <Ruler size={28} color="white" />
                </div>
                <div className="flex-grow-1">
                  <h6 className="mb-1">Mesures</h6>
                  <div className="small text-muted">
                    {dashboard?.customersWithMeasurements || 0} clientes
                  </div>
                </div>
                <ChevronRight size={20} className="text-muted" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============ ALERTES CRITIQUES ============ */}
      <Row className="g-3 mb-4">
        {/* Livraisons aujourd'hui */}
        <Col md={4}>
          <Card 
            className={`alert-card shadow-sm h-100 ${dashboard?.todayDeliveries > 0 ? 'urgent' : ''}`}
            onClick={() => navigate('/atelier/orders')}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <Calendar size={12} />
                    À livrer aujourd'hui
                  </div>
                  <div className="h3 mb-0 mt-1 text-primary">
                    {dashboard?.todayDeliveries || 0}
                  </div>
                </div>
                <div className="alert-icon text-primary">
                  <Calendar size={32} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* En retard */}
        <Col md={4}>
          <Card 
            className={`alert-card shadow-sm h-100 ${dashboard?.overdueOrders > 0 ? 'danger' : ''}`}
            onClick={() => navigate('/atelier/orders')}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <AlertTriangle size={12} />
                    En retard
                  </div>
                  <div className={`h3 mb-0 mt-1 ${dashboard?.overdueOrders > 0 ? 'text-danger' : ''}`}>
                    {dashboard?.overdueOrders || 0}
                  </div>
                </div>
                <div className={`alert-icon ${dashboard?.overdueOrders > 0 ? 'text-danger' : 'text-muted'}`}>
                  <AlertTriangle size={32} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Livraisons ≤ 7j */}
        <Col md={4}>
          <Card 
            className={`alert-card shadow-sm h-100 ${dashboard?.upcomingDeliveries > 0 ? 'warning' : ''}`}
            onClick={() => navigate('/atelier/orders')}
          >
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <Clock size={12} />
                    Livraisons ≤ 7 jours
                  </div>
                  <div className={`h3 mb-0 mt-1 ${dashboard?.upcomingDeliveries > 0 ? 'text-warning' : ''}`}>
                    {dashboard?.upcomingDeliveries || 0}
                  </div>
                </div>
                <div className={`alert-icon ${dashboard?.upcomingDeliveries > 0 ? 'text-warning' : 'text-muted'}`}>
                  <Clock size={32} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============ STATS PAR STATUT ============ */}
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-light d-flex align-items-center gap-2">
              <Target size={18} className="text-primary" />
              <strong>Répartition par statut</strong>
              <Badge bg="secondary" pill className="ms-auto">
                {dashboard?.activeOrders || 0} en cours
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row className="g-2">
                {dashboard?.statusCounts?.map(sc => (
                  <Col key={sc.status} xs={6} md={4} lg={2}>
                    <div className="status-stat text-center p-2 rounded">
                      <div style={{ fontSize: '1.5rem' }}>{sc.icon}</div>
                      <div className="small text-muted">{sc.displayName}</div>
                      <div className="h5 mb-0">{sc.count}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============ STATS FINANCIÈRES ============ */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="stat-card stat-success shadow-sm h-100">
            <Card.Body>
              <div className="small text-muted">CA total</div>
              <div className="h4 text-success mb-0 mt-1">
                {formatPrice(dashboard?.totalRevenue)}
              </div>
              <small className="text-muted">
                {dashboard?.deliveredThisMonth || 0} commandes ce mois
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="stat-card stat-primary shadow-sm h-100">
            <Card.Body>
              <div className="small text-muted">CA ce mois</div>
              <div className="h4 text-primary mb-0 mt-1">
                {formatPrice(dashboard?.monthRevenue)}
              </div>
              <small className="text-muted">
                <TrendingUp size={12} /> Mensuel
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="stat-card stat-info shadow-sm h-100">
            <Card.Body>
              <div className="small text-muted">Total encaissé</div>
              <div className="h4 text-info mb-0 mt-1">
                {formatPrice(dashboard?.totalCollected)}
              </div>
              <small className="text-muted">
                Avances + soldes
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="stat-card stat-warning shadow-sm h-100">
            <Card.Body>
              <div className="small text-muted">Reste à encaisser</div>
              <div className="h4 text-warning mb-0 mt-1">
                {formatPrice(dashboard?.pendingPayments)}
              </div>
              <small className="text-muted">
                Commandes en cours
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============ APERÇUS COMMANDES ============ */}
      <Row className="g-3 mb-4">

        {/* Commandes urgentes */}
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-warning bg-opacity-10 d-flex align-items-center gap-2">
              <AlertTriangle size={18} className="text-warning" />
              <strong>Commandes urgentes</strong>
              <Badge bg="warning" text="dark" pill className="ms-auto">
                {dashboard?.urgentOrders?.length || 0}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {!dashboard?.urgentOrders?.length ? (
                <div className="text-center text-muted py-4 small">
                  ✨ Aucune commande urgente pour le moment
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {dashboard.urgentOrders.map(order => (
                    <OrderLine 
                      key={order.id} 
                      order={order} 
                      onClick={() => navigate('/atelier/orders')}
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Commandes en retard */}
        <Col lg={6}>
          <Card className="shadow-sm h-100">
            <Card.Header className="bg-danger bg-opacity-10 d-flex align-items-center gap-2">
              <AlertTriangle size={18} className="text-danger" />
              <strong>Commandes en retard</strong>
              <Badge bg="danger" pill className="ms-auto">
                {dashboard?.lateOrders?.length || 0}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {!dashboard?.lateOrders?.length ? (
                <div className="text-center text-muted py-4 small">
                  ✅ Aucun retard
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {dashboard.lateOrders.map(order => (
                    <OrderLine 
                      key={order.id} 
                      order={order} 
                      onClick={() => navigate('/atelier/orders')}
                      isLate
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Commandes récentes */}
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary bg-opacity-10 d-flex align-items-center gap-2">
              <TrendingUp size={18} className="text-primary" />
              <strong>Commandes récentes</strong>
              <Button
                variant="link"
                size="sm"
                className="ms-auto p-0"
                onClick={() => navigate('/atelier/orders')}
              >
                Voir toutes <ChevronRight size={14} />
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {!dashboard?.recentOrders?.length ? (
                <div className="text-center text-muted py-4 small">
                  Aucune commande récente. 
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => navigate('/atelier/orders')}
                  >
                    Créer la première
                  </Button>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {dashboard.recentOrders.map(order => (
                    <OrderLine 
                      key={order.id} 
                      order={order} 
                      onClick={() => navigate('/atelier/orders')}
                      showStatus
                    />
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============ STYLES ============ */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .shortcut-card {
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid #e2e8f0;
        }
        .shortcut-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08) !important;
          border-color: #cbd5e1;
        }
        .shortcut-icon {
          width: 56px;
          height: 56px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .alert-card {
          cursor: pointer;
          transition: all 0.2s;
          border-left: 4px solid transparent;
        }
        .alert-card:hover {
          transform: translateY(-1px);
        }
        .alert-card.danger { border-left-color: #dc3545; background: #fff5f5; }
        .alert-card.warning { border-left-color: #f59e0b; background: #fffbeb; }
        .alert-card.urgent { border-left-color: #3b82f6; background: #eff6ff; }

        .status-stat {
          background: #f8fafc;
          transition: all 0.15s;
        }
        .status-stat:hover {
          background: #e2e8f0;
        }

        .stat-card {
          border-top: 3px solid transparent;
        }
        .stat-success { border-top-color: #10b981; }
        .stat-primary { border-top-color: #3b82f6; }
        .stat-info { border-top-color: #06b6d4; }
        .stat-warning { border-top-color: #f59e0b; }
      `}</style>
    </Container>
  );
};

// ============================================================
// ============ COMPOSANT LIGNE DE COMMANDE ==================
// ============================================================

const OrderLine = ({ order, onClick, isLate = false, showStatus = false }) => {
  return (
    <div
      className="list-group-item list-group-item-action"
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div className="flex-grow-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <strong className="small">{order.orderNumber}</strong>
            {showStatus && <OrderStatusBadge status={order.status} size="small" />}
            {order.priority === 'URGENT' && (
              <Badge bg="warning" text="dark" pill style={{ fontSize: '0.65rem' }}>
                🔴 Urgent
              </Badge>
            )}
          </div>
          <div className="small text-muted mt-1">
            <strong>{order.customerName}</strong>
            {order.customerPhone && ` — ${order.customerPhone}`}
          </div>
          <div className={`small mt-1 d-flex align-items-center gap-1 ${
            isLate ? 'text-danger fw-semibold' : 'text-muted'
          }`}>
            <Clock size={12} />
            {customOrderService.formatDaysUntil(order.daysUntilDelivery)}
            {' — '}
            {customOrderService.formatDate(order.expectedDeliveryDate)}
          </div>
        </div>
        <div className="text-end">
          <div className="small text-muted">
            {customOrderService.formatPrice(order.totalPrice)}
          </div>
          <ProgressBar
            now={order.paymentPercentage}
            variant={order.isFullyPaid ? 'success' : 'info'}
            style={{ width: '100px', height: '4px', marginTop: '4px' }}
          />
        </div>
      </div>
    </div>
  );
};

export default AtelierDashboard;